// Polls Spotify for the active instructor's currently-playing track and queue,
// writes both to Firebase. Runs in the instructor's browser tab while they
// have a room claimed. Two heartbeat purposes:
//
//   1. `last_polled_at` on the room → other clients can detect stale claims
//      (auto-release at 10 min if no heartbeat).
//   2. On each tick we re-check `active_instructor` against our own user ID.
//      If someone else has taken over, we stop polling immediately so we
//      don't keep writing into a room we don't own anymore.

import { spotifyGet } from './oauth.js'
import { pushChild, updateNode, fetchOnce } from '../firebase/realtime.js'
import { findDuplicate } from '../utils/fuzzyMatch.js'

// Stricter than the duplicate-warning matcher (0.82). False positives here
// silently vanish a queued request from the dancer view — worse UX than a
// missed warning, so we err conservative.
const AUTO_MARK_THRESHOLD = 0.88

const POLL_INTERVAL_MS = 10_000
const BACKFILL_INTERVAL_MS = 120_000

export class SpotifyRoomPoller {
  /**
   * @param {string}   roomId             "main" or "late"
   * @param {string}   spotifyUserId      the active instructor's Spotify ID (used to detect take-over)
   * @param {number}   venueOpenedAt      timestamp when the night started — backfill ignores anything older
   * @param {function} [onChange]         optional callback when a new track is recorded
   * @param {function} [onTakenOver]      called when this poller detects another instructor took the room
   */
  constructor(roomId, spotifyUserId, venueOpenedAt, onChange, onTakenOver) {
    this.roomId = roomId
    this.spotifyUserId = spotifyUserId
    this.venueOpenedAt = venueOpenedAt || 0
    this.onChange = onChange
    this.onTakenOver = onTakenOver
    this.lastTrackId = null
    this.lastBackfillAt = 0
    this.timer = null
    this.stopped = false
  }

  start() {
    this.stopped = false
    this._tick()
    document.addEventListener('visibilitychange', this._onVisibility)
  }

  stop() {
    this.stopped = true
    if (this.timer) clearTimeout(this.timer)
    document.removeEventListener('visibilitychange', this._onVisibility)
  }

  _onVisibility = () => {
    if (!document.hidden) this._backfill()
  }

  async _tick() {
    if (this.stopped) return
    try {
      // Take-over guard: bail out if we no longer own the room
      const currentClaim = await fetchOnce(`rooms/${this.roomId}/active_instructor`)
      if (currentClaim && currentClaim !== this.spotifyUserId) {
        // eslint-disable-next-line no-console
        console.warn(`[poller] room ${this.roomId} taken over by ${currentClaim}; stopping.`)
        this.onTakenOver?.(currentClaim)
        this.stop()
        return
      }

      // Fetch currently-playing and queue in parallel
      const [nowData, queueData] = await Promise.all([
        spotifyGet('/me/player/currently-playing').catch(() => null),
        spotifyGet('/me/player/queue').catch(() => null),
      ])

      // Heartbeat — always write last_polled_at so other clients see we're alive
      const update = { last_polled_at: Date.now() }

      if (nowData && nowData.item) {
        const track = nowData.item
        if (track.id !== this.lastTrackId) {
          await this._recordPlay(track)
          this.lastTrackId = track.id
        }
        update.now_playing = {
          song_title: track.name,
          artist: (track.artists || []).map((a) => a.name).join(', '),
          album_art: (track.album?.images?.[1] || track.album?.images?.[0])?.url || null,
          spotify_track_id: track.id,
          updated_at: Date.now(),
        }
      }

      if (queueData?.queue) {
        // Show the next 5 upcoming tracks. Keeps the dancer view scannable
        // and Firebase payloads small.
        update.queue = queueData.queue.slice(0, 5).map((track) => ({
          song_title: track.name,
          artist: (track.artists || []).map((a) => a.name).join(', '),
          album_art: (track.album?.images?.[2] || track.album?.images?.[1] || track.album?.images?.[0])?.url || null,
          spotify_track_id: track.id,
        }))
        update.queue_updated_at = Date.now()
      }

      await updateNode(`rooms/${this.roomId}`, update)

      if (Date.now() - this.lastBackfillAt > BACKFILL_INTERVAL_MS) {
        await this._backfill()
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('[spotify poller] tick error:', err.message)
    } finally {
      if (!this.stopped) this.timer = setTimeout(() => this._tick(), POLL_INTERVAL_MS)
    }
  }

  async _backfill() {
    this.lastBackfillAt = Date.now()
    try {
      const data = await spotifyGet('/me/player/recently-played?limit=10')
      if (!data?.items) return
      for (const item of data.items.reverse()) {
        const track = item.track
        if (!track) continue
        const playedAt = new Date(item.played_at).getTime()
        // Privacy guard: never backfill anything that played before the night
        // was opened. Otherwise pre-night listening history could leak into
        // the dancer-visible "Already played" list.
        if (playedAt < this.venueOpenedAt) continue
        const entry = {
          room: this.roomId,
          song_title: track.name,
          artist: (track.artists || []).map((a) => a.name).join(', '),
          spotify_track_id: track.id,
          played_at: playedAt,
          source: 'recently-played-backfill',
        }
        await pushChild('played_tonight', entry)
        // Also auto-mark any open requests (queued OR pending) that match
        // this backfilled play.
        await this._markMatchingRequestsAsPlayed(entry)
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('[spotify poller] backfill error:', err.message)
    }
  }

  async _recordPlay(track) {
    const entry = {
      room: this.roomId,
      song_title: track.name,
      artist: (track.artists || []).map((a) => a.name).join(', '),
      spotify_track_id: track.id,
      played_at: Date.now(),
      source: 'currently-playing',
    }
    await pushChild('played_tonight', entry)
    await this._markMatchingRequestsAsPlayed(entry)
    this.onChange?.(entry)
  }

  /**
   * After a track is recorded as played, scan THIS room's open requests
   * (both `queued` AND `pending`) and update any that fuzzy-match the
   * played track to status "played". This catches two cases:
   *
   *   1. Instructor explicitly marked queued → song plays → auto-flip
   *      to played. (original behavior)
   *   2. Instructor played a song straight from their Spotify queue
   *      WITHOUT first hitting "Mark queued" in the app. We still want
   *      the matching pending request to flip to played so it shows up
   *      correctly in the dancer view and stops eating quota space in
   *      the "to be decided" list.
   *
   * Auto-mark is intentionally SAME-ROOM ONLY. Pending and queued line
   * dance requests are private to their original room — only their own
   * room's plays can mark them as played. Cross-room visibility for
   * `played` line dances is handled at the dancer-view filter level
   * (see `DancerView.roomRequests`), not by mutating other-room records.
   *
   * Uses a strict 0.88 threshold to avoid false-positive removals.
   * For pending → played transitions we also stamp queued_at = played_at
   * so the row's history isn't missing a state.
   */
  async _markMatchingRequestsAsPlayed(playedEntry) {
    try {
      const allRequests = await fetchOnce('requests')
      if (!allRequests) return
      const playedAsArray = [playedEntry]
      const updates = []
      for (const [id, req] of Object.entries(allRequests)) {
        if (req.room !== this.roomId) continue
        if (req.status !== 'queued' && req.status !== 'pending') continue
        const candidate = {
          song_title: req.song_title,
          artist: req.artist,
          spotify_track_id: req.spotify_track_id,
        }
        const matched = findDuplicate(candidate, playedAsArray, AUTO_MARK_THRESHOLD)
        if (matched) {
          const playedAt = playedEntry.played_at || Date.now()
          const patch = {
            status: 'played',
            played_at: playedAt,
            played_match_source: playedEntry.source || 'spotify',
          }
          // For pending → played, fill in queued_at retroactively so the
          // row's lifecycle doesn't have a hole. Queued requests already
          // have queued_at set when the instructor marked them.
          if (req.status === 'pending') {
            patch.queued_at = playedAt
            patch.played_match_source = `${patch.played_match_source}-skipped-queue`
          }
          updates.push(updateNode(`requests/${id}`, patch))
        }
      }
      if (updates.length > 0) await Promise.all(updates)
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('[spotify poller] auto-mark error:', err.message)
    }
  }
}
